import { useAuth } from '../../context/AuthContext';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import MobileBackButton from '../../components/MobileBackButton';

export default function GettingStartedPage() {
  const { isAuthenticated, loading } = useAuth();
  const router = useRouter();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      router.push({
        pathname: '/login',
        query: { redirect: router.asPath }
      });
    }
  }, [isAuthenticated, loading, router]);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="flex-1 p-8 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center mb-6">
          <MobileBackButton />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Getting Started with CreoleCentric</h1>
        </div>

        {/* Introduction */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-6 mb-8 rounded-r-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-500 dark:text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">Welcome to CreoleCentric!</h3>
              <p className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                This guide will walk you through creating your first Haitian Creole text-to-speech synthesis,
                exploring platform features, and mastering the basics of voice selection and audio generation.
              </p>
            </div>
          </div>
        </div>

        {/* Quick Start Steps */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Quick Start: Create Your First TTS</h2>
          </div>
          <div className="p-6">
            <div className="space-y-6">
              {/* Step 1 */}
              <div className="flex">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                  1
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Navigate to Text-to-Speech</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-3">
                    From your dashboard, click on the <strong>"Text to Speech"</strong> option in the sidebar.
                    This opens the main TTS interface where you'll create your audio.
                  </p>
                  <Link href="/text-to-speech" className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium">
                    Go to Text-to-Speech →
                  </Link>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                  2
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Enter Your Text</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-3">
                    Type or paste your text into the editor. You can enter text in any of our 50+ supported languages including:
                  </p>
                  <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1 mb-3">
                    <li>Haitian Creole (direct synthesis)</li>
                    <li>French, English, Spanish (auto-translation)</li>
                    <li>Portuguese, Chinese, Arabic, and more</li>
                  </ul>
                  <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-md">
                    <p className="text-sm text-gray-700 dark:text-gray-300 font-mono">
                      Example: "Bonjou! Kijan ou ye?" or "Hello! How are you?"
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                  3
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Select a Voice</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-3">
                    Choose from our library of authentic Haitian Creole voices. Each voice has unique characteristics:
                  </p>
                  <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1 mb-3">
                    <li><strong>Gender:</strong> Male or Female voices</li>
                    <li><strong>Age:</strong> Young adult, adult, or elder</li>
                    <li><strong>Region:</strong> Port-au-Prince, Cap-Haïtien, Gonaïves, and more</li>
                  </ul>
                  <Link href="/voices" className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium">
                    Browse Voice Library →
                  </Link>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                  4
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Customize Voice Settings (Optional)</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-3">
                    Fine-tune your audio output with advanced controls:
                  </p>
                  <div className="grid md:grid-cols-2 gap-4 mb-3">
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-1">Speed</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">Adjust speaking rate (0.5x to 2.0x)</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-1">Pitch</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">Modify voice pitch (-50% to +50%)</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-1">Emphasis</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">Control word stress and intonation</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-1">Pauses</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">Add natural breaks between phrases</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 5 */}
              <div className="flex">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                  5
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Generate Audio</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-3">
                    Click the <strong>"Generate Audio"</strong> button. Processing typically takes 1-3 seconds per 1,000 characters.
                    You'll see a progress indicator while your audio is being synthesized.
                  </p>
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-3 rounded-r-md">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      <strong>Credit Usage:</strong> Audio generation costs 1 credit per character. Check your credit balance in the top navigation bar.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 6 */}
              <div className="flex">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                  6
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Download Your Audio</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-3">
                    Once generation is complete, you can:
                  </p>
                  <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2 mb-3">
                    <li><strong>Play audio:</strong> Listen directly in your browser with real-time text highlighting</li>
                    <li><strong>Download MP3:</strong> Perfect for most uses (podcasts, videos, websites)</li>
                    <li><strong>Download WAV:</strong> Uncompressed format for professional audio editing</li>
                    <li><strong>Download WebM:</strong> Optimized for web applications</li>
                    <li><strong>Export SRT subtitles:</strong> Sync your audio with video projects</li>
                  </ul>
                  <p className="text-gray-600 dark:text-gray-300">
                    All generated files are saved in your history for 30 days for easy re-download.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Understanding the Interface */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Understanding the Interface</h2>
          </div>
          <div className="p-6">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Main Components</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">📝 Text Editor</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Enter your text with character count and language detection.
                      Supports plain text and basic formatting.
                    </p>
                  </div>
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">🎤 Voice Selector</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Browse and preview voices before selection. Filter by gender,
                      age, and regional accent.
                    </p>
                  </div>
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">⚙️ Settings Panel</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Adjust speed, pitch, emphasis, and pauses.
                      Save presets for consistent output.
                    </p>
                  </div>
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">📊 History Panel</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      View all generated audio with date, duration, and voice used.
                      Quick re-download of previous generations.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Basic Voice Selection Guide */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Basic Voice Selection</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <p className="text-gray-600 dark:text-gray-300">
                Choosing the right voice depends on your use case and target audience. Here's how to select:
              </p>

              <div className="space-y-3">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">🎓 Educational Content</h4>
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    Use clear, moderate-speed voices. Adult voices work best for instructional material.
                    Consider regional accents matching your student population.
                  </p>
                </div>

                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                  <h4 className="font-medium text-green-900 dark:text-green-100 mb-2">📢 Marketing & Announcements</h4>
                  <p className="text-sm text-green-800 dark:text-green-200">
                    Choose energetic, clear voices. Adjust pitch and emphasis for engagement.
                    Male voices often convey authority; female voices create warmth.
                  </p>
                </div>

                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                  <h4 className="font-medium text-purple-900 dark:text-purple-100 mb-2">📖 Storytelling & Narration</h4>
                  <p className="text-sm text-purple-800 dark:text-purple-200">
                    Elder voices add authenticity for traditional stories. Young adult voices suit modern narratives.
                    Experiment with speed and pauses for dramatic effect.
                  </p>
                </div>

                <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                  <h4 className="font-medium text-orange-900 dark:text-orange-100 mb-2">🎙️ Podcasts & Media</h4>
                  <p className="text-sm text-orange-800 dark:text-orange-200">
                    Professional, natural-sounding voices at normal speed. Consider consistency if creating a series.
                    Port-au-Prince accent is most widely understood.
                  </p>
                </div>
              </div>

              <div className="mt-6">
                <Link href="/voices" className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors">
                  Explore All Voices
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Downloading Audio Files */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Downloading Audio Files</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                CreoleCentric offers multiple export formats to suit different needs:
              </p>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                    <span className="text-2xl mr-2">🎵</span>
                    MP3 Format
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                    Compressed audio perfect for podcasts, videos, and websites.
                    Small file size with excellent quality.
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-400 font-medium">Recommended for most users</p>
                </div>

                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                    <span className="text-2xl mr-2">🎧</span>
                    WAV Format
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                    Uncompressed, studio-quality audio for professional editing.
                    Larger file size, highest fidelity.
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">Best for professional work</p>
                </div>

                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                    <span className="text-2xl mr-2">🌐</span>
                    WebM Format
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                    Optimized for web browsers and streaming applications.
                    Great balance of quality and file size.
                  </p>
                  <p className="text-xs text-purple-600 dark:text-purple-400 font-medium">Ideal for web apps</p>
                </div>
              </div>

              <div className="mt-6 bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                  <span className="text-2xl mr-2">📝</span>
                  SRT Subtitles
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                  Export time-synced subtitle files for video projects. Compatible with YouTube, Vimeo,
                  video editors, and media players. Perfect for adding captions to your content.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Explore More Features */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Explore More Features</h2>
          </div>
          <div className="p-6">
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              CreoleCentric offers much more than text-to-speech. Here are some additional platform features to explore:
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              <Link href="/docs/learn-creole" className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors block">
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">Learn Creole</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Interactive lessons with flashcards, quizzes, dialogues, and a Creole Calculator covering 27+ categories.
                </p>
              </Link>

              <Link href="/docs/dictionary" className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors block">
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">Haitian Creole Dictionary</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Comprehensive dictionary with definitions, audio pronunciation, personal entries, and favorites.
                </p>
              </Link>

              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">Text Processing Settings</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Enable <strong>Smart Format</strong> (auto-formats numbers/dates), <strong>Creolize French Words</strong> (suggests Creole alternatives), and <strong>Spellcheck on Generate</strong> in Settings &gt; Text Processing.{' '}
                  <Link href="/docs/advanced-features" className="text-blue-600 dark:text-blue-400 hover:underline">Learn more</Link>
                </p>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">Streaming Mode</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Need instant results? Switch the Processing Mode picker to <strong>Streaming</strong> to hear audio start playing within about a second instead of waiting for the whole clip. Supported by <strong>Creole Regional</strong> and <strong>Creole Studio Pro</strong>. Long inputs are auto-split into ≤600-char phrases that stream sequentially. Billed at 1.5x credits.{' '}
                  <Link href="/docs/advanced-features" className="text-blue-600 dark:text-blue-400 hover:underline">Learn more</Link>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 rounded-lg p-8 text-white">
          <h2 className="text-2xl font-bold mb-4">Ready to Create Amazing Content?</h2>
          <p className="mb-6 text-blue-100">
            You now know the basics of CreoleCentric! Explore advanced features, browse our voice library,
            or dive into API integration for your applications.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            <Link href="/text-to-speech" className="bg-white text-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-blue-50 transition-colors text-center">
              Start Creating
            </Link>
            <Link href="/docs/voices" className="bg-blue-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-400 transition-colors text-center">
              Explore Voices
            </Link>
            <Link href="/docs/advanced-features" className="bg-blue-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-400 transition-colors text-center">
              Advanced Features
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
